import React from 'react';
import {
  Box,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { useNavigate } from 'react-router-dom';
import { getLocalizedString, type LocalizedString } from '../utils/localizedText';

type SideNavTab = {
  label: LocalizedString;
  url: string;
};

type Props = {
  config?: {
    enabled?: boolean;
    buttonTooltip?: LocalizedString;
    triggerRadius?: number;
    popupRadius?: number;
    popoverTitle?: LocalizedString;
    popoverSubtitle?: LocalizedString;
    popoverHint?: LocalizedString;
    tabs?: SideNavTab[];
  };
  language?: string;
};

const PageSideNavigationSection: React.FC<Props> = ({ config, language }) => {
  const navigate = useNavigate();
  const enabled = config?.enabled !== false;
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [tabIndex, setTabIndex] = React.useState<number>(0);

  const tabs = React.useMemo(() => {
    const raw: any[] = (Array.isArray(config?.tabs) ? (config?.tabs as any[]) : []);
    return raw
      .map((t: any) => ({
        label: (typeof t?.label === 'string' ? ({ de: String(t?.label || '') } as any) : (t?.label && typeof t?.label === 'object' ? t.label : ({ de: '' } as any))),
        url: String(t?.url || ''),
      }))
      .filter((t: any) => getLocalizedString(t.label, language || 'de', 'de', '').trim() || t.url.trim());
  }, [config?.tabs, language]);

  React.useEffect(() => {
    if (tabIndex >= tabs.length) setTabIndex(0);
  }, [tabIndex, tabs.length]);

  if (!enabled) return null;

  const tooltip = getLocalizedString(config?.buttonTooltip as any, language || 'de', 'de', 'Menü');
  const popTitle = getLocalizedString((config as any)?.popoverTitle as any, language || 'de', 'de', 'Navigation');
  const popSubtitle = getLocalizedString((config as any)?.popoverSubtitle as any, language || 'de', 'de', 'Schnellzugriff');
  const popHint = getLocalizedString((config as any)?.popoverHint as any, language || 'de', 'de', 'Tippe einen Eintrag, um direkt zu öffnen.');
  const radius = (() => {
    const raw = (config as any)?.triggerRadius;
    const n = typeof raw === 'number' ? raw : (raw === null || raw === undefined ? NaN : Number(raw));
    const v = Number.isFinite(n) ? n : 999;
    return Math.max(0, Math.min(999, v));
  })();
  const popupRadius = (() => {
    const raw = (config as any)?.popupRadius;
    const n = typeof raw === 'number' ? raw : (raw === null || raw === undefined ? NaN : Number(raw));
    const v = Number.isFinite(n) ? n : 16;
    return Math.max(0, Math.min(64, v));
  })();
  const open = Boolean(anchorEl);
  const active = tabs[tabIndex];

  const go = (url: string) => {
    const u = String(url || '').trim();
    if (!u) return;
    try {
      if (/^https?:\/\//i.test(u)) {
        window.open(u, '_blank', 'noopener,noreferrer');
        return;
      }
    } catch {}
    try {
      navigate(u);
    } catch {
      try {
        window.location.href = u;
      } catch {}
    }
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1400,
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ pointerEvents: 'auto' }}>
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            title={tooltip}
            aria-label={tooltip}
            sx={{
              width: 44,
              height: 84,
              borderTopLeftRadius: radius,
              borderBottomLeftRadius: radius,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              color: 'white',
              background: 'linear-gradient(135deg, rgba(59,130,246,0.98), rgba(168,85,247,0.95))',
              boxShadow: '0 18px 44px rgba(2,6,23,0.24)',
              borderLeft: '1px solid rgba(255,255,255,0.22)',
              borderTop: '1px solid rgba(255,255,255,0.22)',
              borderBottom: '1px solid rgba(255,255,255,0.22)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(37,99,235,1), rgba(147,51,234,1))',
                boxShadow: '0 22px 52px rgba(2,6,23,0.30)',
              },
            }}
          >
            <MenuRoundedIcon />
          </IconButton>
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: { xs: 320, sm: 380 },
            borderRadius: popupRadius,
            overflow: 'hidden',
            border: '1px solid rgba(2,6,23,0.08)',
            boxShadow: '0 28px 80px rgba(2,6,23,0.35)',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.88))',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, fontSize: 14, lineHeight: 1.2 }}>{popTitle}</Typography>
            <Typography variant="caption" color="text.secondary">{popSubtitle}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setAnchorEl(null)} title="Schließen">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />

        {tabs.length > 0 ? (
          <>
            <Box sx={{ p: 1.25 }}>
              <List disablePadding sx={{ display: 'grid', gap: 1 }}>
                {tabs.map((t, i) => {
                  const label = getLocalizedString(t.label, language || 'de', 'de', `Tab ${i + 1}`);
                  const selected = i === tabIndex;
                  return (
                    <ListItemButton
                      key={`sidenav-item-${i}`}
                      onClick={() => {
                        setTabIndex(i);
                        go(t.url);
                        setAnchorEl(null);
                      }}
                      selected={selected}
                      sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: selected ? 'rgba(249,115,22,0.55)' : 'rgba(2,6,23,0.08)',
                        background: selected
                          ? 'linear-gradient(90deg, rgba(249,115,22,0.18), rgba(249,115,22,0.05))'
                          : 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.75))',
                        boxShadow: selected ? '0 12px 28px rgba(249,115,22,0.18)' : '0 10px 24px rgba(2,6,23,0.08)',
                        py: 1.25,
                        '&:hover': {
                          background: 'linear-gradient(90deg, rgba(59,130,246,0.12), rgba(168,85,247,0.08))',
                        },
                      }}
                    >
                      <ListItemText
                        primary={label}
                        secondary={t.url || ''}
                        primaryTypographyProps={{ sx: { fontWeight: 950, fontSize: 14, lineHeight: 1.2 } }}
                        secondaryTypographyProps={{ sx: { color: 'text.secondary', fontSize: 12, mt: 0.25, wordBreak: 'break-word' } }}
                      />
                      <OpenInNewRoundedIcon fontSize="small" />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
            <Divider />
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {popHint}
              </Typography>
            </Box>
          </>
        ) : (
          <Box sx={{ p: 2.25 }}>
            <Typography sx={{ fontWeight: 900, mb: 0.75 }}>Keine Tabs konfiguriert</Typography>
            <Typography variant="body2" color="text.secondary">
              Öffne den Editor dieser Komponente und füge Tabs hinzu.
            </Typography>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default PageSideNavigationSection;
